import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Icon, { AvailableIconTypes } from '@ui/Icon';
import Typography from '@ui/Typography';

interface ModelOptionProps extends Omit<ModelOptionContentProps, 'title'> {
  variant: 'primary' | 'secondary' | 'tertiary';
  title: string | React.ReactNode;
}

const useStyles = makeStyles()(theme => ({
  container: {
    backgroundColor: palette.gray_900,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(2),
    display: 'grid',
    gridAutoFlow: 'column',
    gap: theme.spacing(1.5),
    justifyContent: 'start',
    alignItems: 'center',
    gridTemplateColumns: 'auto 1fr',
    outline: `1px solid ${palette.gray_700}`,
    '&[data-disabled="true"]': { color: palette.gray_75 },
  },
  noIcon: {
    paddingLeft: theme.spacing(3),
  },
  innerContent: { display: 'grid', gap: theme.spacing(0.5) },

  tertiaryContainer: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gridAutoFlow: 'column',
    gap: theme.spacing(1.5),
    alignItems: 'start',
  },
  tertiaryPanel: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing(1),
  },
  tertiaryTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },

  inactive: { color: palette.gray_75 },
  active: { color: palette.gray_0 },
  disabled: { opacity: 0.5 },
}));

interface ModelOptionContentProps
  extends Pick<ModelOptionContainerProps, 'disabled'> {
  icon?: AvailableIconTypes;
  title: string | React.ReactNode;
  description?: string;
  children?: React.ReactNode;
  active?: boolean;
}

interface ModelOptionContainerProps {
  disabled?: boolean;
  icon?: AvailableIconTypes;
  children: React.ReactNode;
  className?: string;
}

const ModelOptionContainer = (props: ModelOptionContainerProps) => {
  const { disabled, children, className, icon } = props;
  const { classes, cx } = useStyles();
  return (
    <Box
      data-disabled={disabled}
      className={cx(className, classes.container, {
        [classes.noIcon]: icon === undefined,
      })}
    >
      {children}
    </Box>
  );
};

/**
 * TODO: Should probably be converged w <Vertical/> from onboarding work
 */
const ModelOptionLayout1 = (props: ModelOptionContentProps) => {
  const { classes, cx } = useStyles();
  const { icon, title, description, disabled, active, children } = props;
  return (
    <ModelOptionContainer disabled={disabled} icon={icon}>
      {icon && (
        <Box display="flex" alignItems="center">
          <Icon
            type={icon}
            fontSize="medium"
            className={cx(classes.inactive, { [classes.active]: active })}
          />
        </Box>
      )}
      <Box className={classes.innerContent}>
        {typeof title === 'string' ? (
          <Typography
            id="models-label"
            variant="pMd"
            component="h3"
            bold
            className={cx(classes.inactive, {
              [classes.active]: active,
              [classes.disabled]: disabled,
            })}
          >
            {title}
          </Typography>
        ) : (
          title
        )}
        <Typography
          variant="pSm"
          color={active ? 'primary' : 'secondary'}
          className={cx({
            [classes.disabled]: disabled,
          })}
        >
          {description}
        </Typography>
      </Box>
      {children}
    </ModelOptionContainer>
  );
};

/**
 * Secondary variant which allows for rendering children in the title slot
 * title={<div>Some children where the title is placed</div>}
 */
const ModelOptionLayout2 = (
  props: Omit<ModelOptionContentProps, 'title'> & { title: React.ReactNode }
) => {
  const { classes, cx } = useStyles();
  const { icon, title, description, disabled, active } = props;
  return (
    <ModelOptionContainer disabled={disabled} icon={icon}>
      {icon && (
        <Box display="flex" alignItems="center">
          <Icon
            type={icon}
            fontSize="medium"
            className={cx(classes.inactive, {
              [classes.active]: active,
              [classes.disabled]: disabled,
            })}
          />
        </Box>
      )}
      <Box className={classes.innerContent}>
        {title}
        <Typography
          variant="pSm"
          color={active ? 'primary' : 'secondary'}
          className={cx({ [classes.disabled]: disabled })}
        >
          {description}
        </Typography>
      </Box>
    </ModelOptionContainer>
  );
};

/**
 * Tertiary variant which allows for rendering children below the title line
 */
const ModelOptionLayout3 = (
  props: Omit<ModelOptionContentProps, 'description' | 'title'> & {
    title: string | React.ReactNode;
  }
) => {
  const { classes, cx } = useStyles();
  const { icon, title, children, disabled, active } = props;

  const isTitleStringType = typeof title === 'string';

  return (
    <ModelOptionContainer
      className={classes.tertiaryContainer}
      disabled={disabled}
      icon={icon}
    >
      {icon && (
        <Icon
          type={icon}
          fontSize="medium"
          className={cx(classes.inactive, {
            [classes.active]: active,
            [classes.disabled]: disabled,
          })}
        />
      )}
      <Box className={classes.tertiaryPanel}>
        <Box
          className={cx(classes.tertiaryTitle, {
            [classes.inactive]: !active,
            [classes.active]: active,
          })}
        >
          {isTitleStringType ? (
            <Typography id="models-label" variant="pMd" component="h3" bold>
              {title}
            </Typography>
          ) : (
            title
          )}
        </Box>
        <Box>{children}</Box>
      </Box>
    </ModelOptionContainer>
  );
};

const ModelOption = (props: ModelOptionProps) => {
  const { variant, children, ...rest } = props;
  const { title, ...restWithoutTitle } = props;

  if (variant === 'secondary') return <ModelOptionLayout2 {...rest} />;

  if (variant === 'tertiary') {
    return (
      <ModelOptionLayout3 title={title} {...restWithoutTitle}>
        {children}
      </ModelOptionLayout3>
    );
  }

  return (
    <ModelOptionLayout1 title={title} {...restWithoutTitle}>
      {children}
    </ModelOptionLayout1>
  );
};

export default ModelOption;
